import { Helmet } from "react-helmet-async";

const SITE = "https://verde2050.com";
const DEFAULT_OG = `${SITE}/og/home.jpg`;
const DEFAULT_OG_WIDTH = "1200";
const DEFAULT_OG_HEIGHT = "630";
const DEFAULT_OG_ALT = "Verde 2050 — Built for Tomorrow. Land development & investment in Greater Lisbon.";
const LOGO = `${SITE}/verde2050-logo-green.png`;

// Site-wide graph — Organization + WebSite. Rendered on every page so
// AI crawlers (GPTBot, ClaudeBot, PerplexityBot, Google-Extended) can build
// an accurate model of Verde 2050 no matter where they land.
const SITE_LD = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Organization",
            "@id": `${SITE}/#organization`,
            "name": "Verde 2050",
            "url": SITE,
            "logo": { "@type": "ImageObject", "url": LOGO, "caption": "Verde 2050" },
            "image": DEFAULT_OG,
            "slogan": "Built for Tomorrow",
            "description":
                "Verde 2050 is an independent, founder-led real estate development and investment company in Portugal. It identifies land, takes it through planning and licensing, and sells approved projects to developers — investing typically 5–10% of project equity alongside partners. It also helps buyers acquire land and, through Land Intelligence, helps developers implement its AI framework for smarter development decisions.",
            "founder": { "@id": `${SITE}/about#pieter-van-herpen` },
            "email": "hello@verde2050.com",
            "address": { "@type": "PostalAddress", "addressRegion": "Greater Lisbon", "addressCountry": "PT" },
            "areaServed": ["Greater Lisbon", "Cascais", "Sintra", "Oeiras", "Lisbon", "Portugal"],
            "knowsAbout": [
                "Land development Portugal", "Real estate development Portugal", "Land investment Portugal",
                "Property development Sintra", "Development land Cascais", "Licensed development projects Portugal",
                "Land acquisition Portugal", "Land Intelligence",
            ],
            "sameAs": ["https://news.verde2050.com", "https://www.linkedin.com/company/verde-2050"],
            "contactPoint": { "@type": "ContactPoint", "email": "hello@verde2050.com", "contactType": "general inquiry", "availableLanguage": ["English", "Portuguese", "Dutch"] },
        },
        {
            "@type": "Person",
            "@id": `${SITE}/about#pieter-van-herpen`,
            "name": "Pieter van Herpen",
            "jobTitle": "Founder, Verde 2050; Co-founder, Verde Capital",
            "worksFor": { "@id": `${SITE}/#organization` },
            "description": "Entrepreneur who built and sold technology businesses before turning to real estate development and investment in Portugal.",
        },
        {
            "@type": "WebSite",
            "@id": `${SITE}/#website`,
            "url": SITE,
            "name": "Verde 2050",
            "description": "Verde 2050 — Built for Tomorrow. Founder-led real estate development and investment in Greater Lisbon, Portugal.",
            "publisher": { "@id": `${SITE}/#organization` },
            "inLanguage": "en",
        },
    ],
};

export default function SEO({
    title,
    description,
    path = "/",
    og,
    image: imageProp,
    imageWidth,
    imageHeight,
    imageAlt,
    keywords,
    noindex = false,
    jsonLd,
    children,
}) {
    const url = `${SITE}${path}`;
    const fullTitle = title.includes("Verde 2050") || title.includes("Verde Investing") || title.includes("Taylor Brokers")
        ? title
        : `${title} — Verde 2050`;

    const image = og ? `${SITE}/og/${og}.jpg` : imageProp ? (imageProp.startsWith("http") ? imageProp : `${SITE}${imageProp}`) : DEFAULT_OG;
    const isDefaultImage = Boolean(og) || image === DEFAULT_OG;
    const ogWidth = imageWidth || (isDefaultImage ? DEFAULT_OG_WIDTH : undefined);
    const ogHeight = imageHeight || (isDefaultImage ? DEFAULT_OG_HEIGHT : undefined);
    const ogAlt = imageAlt || (path === "/" ? DEFAULT_OG_ALT : `${title} — Verde 2050, Built for Tomorrow`);

    // Per-page WebPage node — helps AI models correctly attribute copy per URL.
    const pageLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        "url": url,
        "name": fullTitle,
        "description": description,
        "inLanguage": "en",
        "isPartOf": { "@id": `${SITE}/#website` },
        "primaryImageOfPage": image,
        "publisher": { "@id": `${SITE}/#organization` },
    };

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:site_name" content="Verde 2050" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:image:type" content="image/jpeg" />
            {ogWidth && <meta property="og:image:width" content={ogWidth} />}
            {ogHeight && <meta property="og:image:height" content={ogHeight} />}
            {ogAlt && <meta property="og:image:alt" content={ogAlt} />}
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@verde2050" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            {ogAlt && <meta name="twitter:image:alt" content={ogAlt} />}

            {/* AI crawler hints */}
            <meta name="ai-content-declaration" content="human-authored" />

            {/* Site-wide Organization + WebSite (once per page load) */}
            <script type="application/ld+json">{JSON.stringify(SITE_LD)}</script>
            {/* Per-page WebPage */}
            <script type="application/ld+json">{JSON.stringify(pageLd)}</script>

            {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            {children}
        </Helmet>
    );
}
